import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Home, Car, Heart, MessageCircle, LifeBuoy, MapPin, CheckCircle, Sparkles, Award, Flag, HandHeart, TrendingUp } from 'lucide-react';
import { organizationInfo, services, prelaunchInfo, impactInfo, supportGroupAnnouncement, roadmapInfo } from '../mockData';
import sagedLogo from '../assets/saged_logo.png';
import arcoLogo from '../assets/arco_logo.png';

const HomePage = () => {
  const serviceIcons = {
    "Peer Recovery Coaching": Users,
    "Recovery Housing Support": Home,
    "Transportation Services": Car,
    "Community Programming": Heart
  };

  const helpIcons = [MessageCircle, LifeBuoy, MapPin];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <img
            src={sagedLogo}
            alt="Saged Recovery and Wellness Collaborative"
            className="mx-auto mb-8 h-28 sm:h-36 w-auto"
            data-testid="hero-logo"
          />
          <span
            data-testid="prelaunch-badge"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-semibold mb-6"
          >
            <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            {prelaunchInfo.badge}
          </span>
          <h1 className="hero-title">
            {prelaunchInfo.headline}
          </h1>
          <p className="hero-subtitle">
            {prelaunchInfo.message}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <Link to="/resources" className="btn-primary flex items-center gap-2" data-testid="hero-resources-btn">
              Explore Resources
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-contact-btn">
              Ask a Question
            </Link>
          </div>
        </div>
      </section>

      {/* Support Group Announcement */}
      <section className="py-10 bg-green-600">
        <div className="container">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left" data-testid="support-group-announcement">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-wide flex-shrink-0">
              <Sparkles className="h-3.5 w-3.5" />
              {supportGroupAnnouncement.badge}
            </span>
            <p className="text-white font-semibold text-base md:text-lg">
              {supportGroupAnnouncement.title}
            </p>
            <Link to="/contact" className="flex-shrink-0 inline-flex items-center gap-1 text-white underline underline-offset-4 text-sm font-medium hover:text-green-100">
              Be the first to know <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Impact So Far */}
      <section className="py-16" data-testid="impact-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Impact So Far</h2>
            <p className="body-large max-w-2xl mx-auto">
              We're already out in the community — raising funds, spreading hope, and supporting our neighbors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {impactInfo.stats.map((stat, index) => (
              <div key={index} className="text-center bg-green-50 rounded-lg p-8" data-testid={`impact-stat-${index}`}>
                <p className="text-4xl font-bold text-green-700 mb-2">{stat.number}</p>
                <p className="body-medium font-medium">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {impactInfo.activities.map((activity, index) => {
              const impactIcons = [Award, Flag, HandHeart, TrendingUp];
              const Icon = impactIcons[index] || Heart;
              return (
                <div key={index} className="service-card" data-testid={`impact-activity-${index}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 rounded-full bg-green-100 flex-shrink-0">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="service-card-title">{activity.title}</h3>
                  </div>
                  <p className="service-card-description">{activity.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Where We're Headed */}
      <section className="py-16 bg-white" data-testid="roadmap-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">{roadmapInfo.title}</h2>
            <p className="body-large max-w-2xl mx-auto">{roadmapInfo.intro}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
            {roadmapInfo.programs.map((program, index) => {
              const roadmapIcons = [Car, LifeBuoy, Users, MessageCircle, Home];
              const Icon = roadmapIcons[index] || Heart;
              return (
                <div key={index} className="service-card" data-testid={`roadmap-program-${index}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 rounded-full bg-green-100 flex-shrink-0">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="service-card-title">{program.title}</h3>
                  </div>
                  <p className="service-card-description">{program.description}</p>
                </div>
              );
            })}
          </div>

          <div className="max-w-3xl mx-auto bg-green-50 border border-green-200 rounded-lg p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="roadmap-membership-note">
            <img src={arcoLogo} alt="ARCO — Alliance for Recovery Centered Organizations" className="h-16 w-auto flex-shrink-0" data-testid="arco-logo-home" />
            <div>
              <p className="body-small font-semibold text-green-700 uppercase tracking-wide mb-1">{roadmapInfo.membershipLabel}</p>
              <p className="body-medium">{roadmapInfo.membershipNote}</p>
              <a href={roadmapInfo.certificateFile} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 body-small text-green-700 font-semibold hover:text-green-800" data-testid="arco-certificate-link">
                View our membership certificate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Recruitment Callout */}
      <section className="py-16 bg-gray-50" data-testid="volunteer-callout">
        <div className="container text-center">
          <h2 className="heading-2 mb-4">We Need Volunteers!</h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            We're actively looking for <span className="font-semibold">Peer Recovery Coaches</span> and{' '}
            <span className="font-semibold">Transporters/Drivers</span> to help us support our neighbors in
            recovery. If you have a caring heart and a little time to give, we'd love to hear from you.
          </p>
          <Link to="/get-involved#volunteer" className="btn-primary inline-flex items-center gap-2" data-testid="home-volunteer-btn">
            <Users className="h-4 w-4" />
            Become a Volunteer
          </Link>
        </div>
      </section>

      {/* How We Can Help Now */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">How We Can Help You Right Now</h2>
            <p className="body-large max-w-2xl mx-auto">
              Support is just a click away — any time you need it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {prelaunchInfo.helpNow.map((item, index) => {
              const Icon = helpIcons[index] || Heart;
              return (
                <div key={index} className="service-card text-center" data-testid={`help-now-${index}`}>
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-green-100">
                      <Icon className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h3 className="service-card-title mb-3">{item.title}</h3>
                  <p className="service-card-description">{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/resources" className="btn-primary">
              View All Resources
            </Link>
          </div>
        </div>
      </section>

      {/* What We're Building */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Programs</h2>
            <p className="body-large max-w-2xl mx-auto">
              We're creating holistic, peer-driven recovery support across Manistee and Mason Counties.
              Here's a look at the programs at the heart of our mission.
            </p>
          </div>

          <div className="ai-grid">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.title] || Users;
              return (
                <div key={service.id} className="service-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-full bg-green-100">
                      <IconComponent className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="service-card-title">{service.title}</h3>
                  </div>
                  <p className="service-card-description mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 body-small">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs" className="btn-secondary">
              Learn More About Our Programs
            </Link>
          </div>
        </div>
      </section>

      {/* Support / Donate CTA */}
      <section className="py-16 bg-green-50">
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Help Us Grow Our Impact</h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            We're building something meaningful for our community — and we'd love your help. Your donation
            fuels recovery support that will reach individuals and families for years to come.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved" className="btn-primary flex items-center gap-2" data-testid="home-donate-btn">
              <Heart className="h-4 w-4" />
              Donate to Our Mission
            </Link>
            <a href={`mailto:${organizationInfo.email}`} className="btn-secondary">
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
